// src/components/DocumentForgery/Batch/BatchResult.jsx
// ─────────────────────────────────────────────────────
// Polls the batch result endpoint and shows per-item progress,
// final stats, and a CSV download button. Navigates to DocumentAnalysis
// when an individual document is clicked.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Download, RefreshCw, CheckCircle, XCircle,
  AlertTriangle, Clock, FileText, ChevronRight, BarChart2,
} from 'react-feather';
import { getBatchResultHandler } from '../../../services/documentForgery';
import { showErrorToast } from '../../../utility/helper';

// ── helpers ───────────────────────────────────────────────────────────────────
const riskColor = (score) => {
  if (!score && score !== 0) return { color: '#6b7280', bg: '#f3f4f6' };
  if (score >= 70) return { color: '#dc2626', bg: '#fee2e2' };
  if (score >= 40) return { color: '#d97706', bg: '#fef3c7' };
  return { color: '#16a34a', bg: '#dcfce7' };
};

const riskLabel = (score) => {
  if (!score && score !== 0) return '—';
  if (score >= 90) return 'CRITICAL';
  if (score >= 70) return 'HIGH';
  if (score >= 40) return 'MEDIUM';
  return 'LOW';
};

const fmtDuration = (sec) => {
  if (!sec) return '—';
  if (sec < 60)  return `${Math.round(sec)}s`;
  return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
};

const fmtCost = (val) => {
  if (!val) return '$0.00';
  return `$${parseFloat(val).toFixed(4)}`;
};

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusChip = ({ status, size = 'md' }) => {
  const cfg = {
    pending:    { color: '#6b7280', bg: '#f3f4f6', label: 'Pending',    icon: <Clock size={11} /> },
    running:    { color: '#2563eb', bg: '#dbeafe', label: 'Running',    icon: <RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> },
    completed:  { color: '#16a34a', bg: '#dcfce7', label: 'Completed',  icon: <CheckCircle size={11} /> },
    partial:    { color: '#d97706', bg: '#fef3c7', label: 'Partial',    icon: <AlertTriangle size={11} /> },
    failed:     { color: '#dc2626', bg: '#fee2e2', label: 'Failed',     icon: <XCircle size={11} /> },
    processing: { color: '#2563eb', bg: '#dbeafe', label: 'Processing', icon: <RefreshCw size={11} style={{ animation: 'spin 1s linear infinite' }} /> },
    queued:     { color: '#6b7280', bg: '#f3f4f6', label: 'Queued',     icon: <Clock size={11} /> },
    skipped:    { color: '#9ca3af', bg: '#f9fafb', label: 'Skipped',    icon: <AlertTriangle size={11} /> },
  };
  const c = cfg[status?.toLowerCase()] || cfg.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: c.bg, color: c.color,
      border: `1px solid ${c.color}33`,
      borderRadius: 20, fontWeight: 700,
      padding: size === 'sm' ? '2px 8px' : '4px 12px',
      fontSize: size === 'sm' ? 11 : 12, whiteSpace: 'nowrap',
    }}>
      {c.icon}{c.label}
    </span>
  );
};

// ── Progress bar ──────────────────────────────────────────────────────────────
const ProgressBar = ({ pct, status }) => {
  const color = status === 'failed' ? '#dc2626' : status === 'completed' ? '#16a34a' : '#44ace4';
  return (
    <div style={{ height: 8, background: '#e5e7eb', borderRadius: 99, overflow: 'hidden', marginBottom: 4 }}>
      <div style={{
        height: '100%', width: `${Math.max(pct, 2)}%`,
        background: color, borderRadius: 99,
        transition: 'width 0.6s ease',
      }} />
    </div>
  );
};

// ── Stat tile ─────────────────────────────────────────────────────────────────
const StatTile = ({ label, value, sub, color = '#111827' }) => (
  <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
    <div style={{ fontSize: 26, fontWeight: 800, color, marginBottom: 2 }}>{value}</div>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
    {sub && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── Item row ──────────────────────────────────────────────────────────────────
const ItemRow = ({ item, onOpen }) => {
  const rc = riskColor(item.overall_risk_score);
  const isClickable = item.status === 'completed' && item.document_id;

  return (
    <div
      onClick={isClickable ? () => onOpen(item.document_id) : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px', borderRadius: 8,
        background: '#fff', border: '1px solid #e5e7eb',
        marginBottom: 6, cursor: isClickable ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, border-color 0.15s',
      }}
      onMouseEnter={e => { if (isClickable) { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#44ace4'; }}}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
    >
      {/* File icon */}
      <div style={{
        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
        background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <FileText size={16} color="#44ace4" />
      </div>

      {/* Name + status */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.file_name}
        </div>
        {item.error_detail ? (
          <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>{item.error_detail}</div>
        ) : (
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            {item.issues_count != null ? `${item.issues_count} issue${item.issues_count !== 1 ? 's' : ''} found` : '—'}
          </div>
        )}
      </div>

      {/* Risk score */}
      {item.overall_risk_score != null && (
        <div style={{
          background: rc.bg, color: rc.color,
          borderRadius: 20, fontSize: 11, fontWeight: 800,
          padding: '3px 10px', whiteSpace: 'nowrap',
        }}>
          {Math.round(item.overall_risk_score)} · {riskLabel(item.overall_risk_score)}
        </div>
      )}

      {/* Status */}
      <StatusChip status={item.status} size="sm" />

      {/* Arrow */}
      {isClickable && <ChevronRight size={16} color="#9ca3af" />}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const BatchResult = () => {
  const { refId }  = useParams();
  const location   = useLocation();
  const navigate   = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const pollRef   = useRef(null);
  const TERMINAL  = new Set(['completed', 'partial', 'failed']);

  // ── Poll ────────────────────────────────────────────────────────────────────
  const fetch = useCallback(async () => {
    try {
      const res = await getBatchResultHandler(refId);
      setData(res.data);
      setError('');
      if (TERMINAL.has(res.data?.status)) {
        clearInterval(pollRef.current);
      }
    } catch (err) {
      setError('Failed to load batch result. Retrying…');
    } finally {
      setLoading(false);
    }
  }, [refId]);

  useEffect(() => {
    fetch();
    pollRef.current = setInterval(fetch, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetch]);

  const isTerminal = TERMINAL.has(data?.status);

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading && !data) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12 }}>
      <RefreshCw size={28} color="#44ace4" style={{ animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#6b7280', fontSize: 14 }}>Loading batch status…</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const pct       = data?.progress_pct ?? 0;
  const items     = data?.items || [];
  const tokenSum  = data?.token_summary || {};

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
        <button onClick={() => navigate('/batch-upload')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px', color: '#6b7280', display: 'flex', borderRadius: 8, marginTop: 2 }}>
          <ArrowLeft size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: '#111827' }}>Batch Analysis</h2>
            {data && <StatusChip status={data.status} />}
            {!isTerminal && (
              <span style={{ fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 4 }}>
                <RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Auto-refreshing every 4s
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4, fontFamily: 'monospace' }}>{refId}</div>
        </div>
        {data?.csv_url && (
          <a href={data.csv_url} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8,
              background: '#16a34a', color: '#fff',
              fontWeight: 700, fontSize: 13, textDecoration: 'none',
              flexShrink: 0,
            }}>
            <Download size={14} /> Download CSV
          </a>
        )}
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#fff5f5', border: '1px solid #fecaca', marginBottom: 16 }}>
          <AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: '#dc2626' }}>{error}</span>
        </div>
      )}

      {/* ── Progress ── */}
      {data && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
              Progress — {data.completed_count + data.failed_count + data.skipped_count} of {data.total_documents} documents
            </span>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#44ace4' }}>{pct.toFixed(0)}%</span>
          </div>
          <ProgressBar pct={pct} status={data.status} />
          <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
            {[
              { label: 'Completed', val: data.completed_count, color: '#16a34a' },
              { label: 'Failed',    val: data.failed_count,    color: '#dc2626' },
              { label: 'Skipped',   val: data.skipped_count,   color: '#9ca3af' },
            ].map(s => s.val > 0 && (
              <span key={s.label} style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>
                {s.label}: {s.val}
              </span>
            ))}
            {data.duration_seconds && (
              <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
                ⏱ {fmtDuration(data.duration_seconds)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Summary stats (terminal only) ── */}
      {isTerminal && data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
          <StatTile label="Documents"   value={data.total_documents}    />
          <StatTile label="Completed"   value={data.completed_count}    color="#16a34a" />
          <StatTile label="Failed"      value={data.failed_count}       color={data.failed_count > 0 ? '#dc2626' : '#111827'} />
          <StatTile label="Total Cost"  value={fmtCost(data.total_cost_usd)} sub={`${tokenSum.total?.toLocaleString() || 0} tokens`} color="#7c3aed" />
        </div>
      )}

      {/* ── Items list ── */}
      {data && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <BarChart2 size={14} color="#44ace4" />
            Documents
            <span style={{ fontWeight: 400, color: '#9ca3af' }}>({items.length})</span>
          </div>

          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: 13 }}>
              {data.status === 'pending' ? 'Waiting for processing to start…' : 'No documents found.'}
            </div>
          ) : (
            <div>
              {items.map((item, i) => (
                <ItemRow
                  key={i}
                  item={item}
                  onOpen={(docId) => navigate(`/dashboard/analysis/${docId}`)}
                />
              ))}
            </div>
          )}

          {/* CSV tip */}
          {isTerminal && !data.csv_url && (
            <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 12 }}>
              CSV report generation in progress…
            </div>
          )}
          {data?.csv_url && (
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 4 }}>
                ✓ CSV Report Ready
              </div>
              <div style={{ fontSize: 12, color: '#166534' }}>
                Full report with extracted features, issues, QR data and cost breakdown.{' '}
                <a href={data.csv_url} target="_blank" rel="noopener noreferrer" style={{ color: '#15803d', fontWeight: 700 }}>
                  Download now →
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BatchResult;